// src/routes/dashboard/index.tsx
/**
 * Dashboard operativa (home): cosa richiede attenzione oggi. Dati reali da
 * GET /api/Dashboard/home, filtrati per ruolo lato backend. Lo storico e i trend a lungo
 * termine restano su /analytics: qui c'è solo l'operativo del momento più "Le mie task".
 */
import React, { useRef } from 'react';
import { Container, SimpleGrid, Grid, Stack, Alert, Button } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { useHomeDashboard } from '../../hooks/use-home-dashboard';
import { DashboardHeader } from './components/DashboardHeader';
import { KpiRow } from './components/KpiRow';
import { PipelineSection } from './components/PipelineSection';
import { RequestDeadlineList } from './components/RequestDeadlineList';
import { MyTasksCard } from './components/MyTasksCard';
import { FlowChart } from './components/FlowChart';
import { RecentRequestsList } from './components/RecentRequestsList';
import { DashboardSkeleton } from './components/DashboardSkeleton';

export function Dashboard() {
  const { data, isLoading, isFetching, isError, refetch } = useHomeDashboard();
  const overdueRef = useRef<HTMLDivElement>(null);

  const scrollToOverdue = () => {
    overdueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <Container size="xl" py="xl">
      <DashboardHeader
        generatedAtUtc={data?.generatedAtUtc}
        canSeeAnalytics={data?.scope.canSeeAnalytics ?? false}
        isFetching={isFetching}
        onRefresh={() => refetch()}
      />

      {isLoading ? (
        <DashboardSkeleton />
      ) : isError || !data ? (
        <Alert color="red" icon={<IconAlertCircle size={18} />} title="Impossibile caricare la dashboard">
          <Stack gap="sm">
            Si è verificato un errore nel recupero dei dati. Riprova.
            <Button size="xs" variant="light" color="red" onClick={() => refetch()} w={120}>
              Riprova
            </Button>
          </Stack>
        </Alert>
      ) : (
        <Stack gap="lg">
          <KpiRow kpis={data.kpis} onOverdueClick={scrollToOverdue} />

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg">
            {data.pipeline.map((section) => (
              <PipelineSection key={section.key} section={section} />
            ))}
          </SimpleGrid>

          <Grid ref={overdueRef}>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <RequestDeadlineList
                title="In ritardo"
                items={data.overdue}
                totalCount={data.kpis.overdueCount}
                emptyLabel="Nessuna richiesta in ritardo"
                tone="danger"
              />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <RequestDeadlineList
                title="In scadenza (7 giorni)"
                items={data.dueSoon}
                totalCount={data.kpis.dueSoonCount}
                emptyLabel="Nessuna scadenza nei prossimi 7 giorni"
                tone="warning"
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={{ base: 12, md: 7 }}>
              <FlowChart data={data.monthlyFlow} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 5 }}>
              <MyTasksCard myTasks={data.myTasks} />
            </Grid.Col>
          </Grid>

          <RecentRequestsList items={data.recent} />
        </Stack>
      )}
    </Container>
  );
}
